import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useOAuth } from '../hooks/useOAuth';
import { useLanguage } from '../i18n/LanguageContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { OAUTH_ENABLED } from '../config/oauth';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';

const Login: React.FC = () => {
  const { login, isAuthenticated, isLoading, user } = useAuth();
  const { oauthUser } = useOAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isAuthenticated && user) {
    // Force password change on first login
    if (user.mustChangePassword) {
      return <Navigate to="/change-password" replace />;
    }
    // Redirect based on user role
    // Support both 'S' (database code) and 'supervisor' (full name)
    if (user.role === 'S' || user.role === 'supervisor') {
      return <Navigate to="/supervisor" replace />;
    }
    // Admin ('A') and other roles go to admin dashboard
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const success = await login(formData);
    if (success) {
      // Navigation will happen automatically via AuthProvider
    }
    
    setIsSubmitting(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      {/* Language Switcher - Top Right */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher variant="dropdown" />
      </div>
      
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* Way Fong Logo */}
          <div className="mx-auto w-fit mb-6">
            <img
              src="/wf-removebg-preview.png"
              alt="Way Fong"
              className="h-32 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-secondary-900 dark:text-white">
            Production Management Platform
          </h2>
          <p className="mt-2 text-center text-sm text-secondary-600 dark:text-secondary-400">
            {t('auth.login')}
          </p>

          {/* OAuth user badge - shows the authenticated user from WayFong Auth */}
          {OAUTH_ENABLED && oauthUser?.email && (
            <div className="mt-4 flex items-center justify-center">
              <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-sm">
                <CheckCircle className="h-4 w-4 mr-2" />
                <span>{t('oauth.loggedInAs')} {oauthUser.email}</span>
              </div>
            </div>
          )}
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                {t('auth.username')}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="mt-1 input-field"
                placeholder={t('auth.username')}
                value={formData.username}
                onChange={handleInputChange}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                {t('auth.password')}
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="input-field pr-10"
                  placeholder={t('auth.password')}
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-secondary-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-secondary-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('common.loading')}
                </div>
              ) : (
                t('auth.loginButton')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;